import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search, Filter, Download, Eye, TrendingUp, AlertTriangle } from 'lucide-react'
import { BusinessSearchFilters } from '@/components/lender/BusinessSearchFilters'
import { BusinessSearchResults } from '@/components/lender/BusinessSearchResults'
import { BusinessQuickView } from '@/components/lender/BusinessQuickView'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useToast } from '@/components/ui/MobileToast'
import { Business, TLBHScore } from '@/types'
import { businessService, BusinessListItem } from '@/services/businessService'

interface BusinessSearchResult {
  business: Business
  score: TLBHScore
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
  lastUpdated: string
  alerts: number
  trend: 'improving' | 'stable' | 'declining'
}

// Helper function to convert API response to component format
const convertApiToBusinessResult = (item: BusinessListItem): BusinessSearchResult => {
  const score = parseFloat(item.score || '0')
  const band = item.band || 'C'
  
  // Determine risk level based on score
  let riskLevel: 'low' | 'medium' | 'high' | 'critical' = 'medium'
  if (score >= 85) riskLevel = 'low'
  else if (score >= 75) riskLevel = 'low'
  else if (score >= 65) riskLevel = 'medium'
  else if (score >= 55) riskLevel = 'high'
  else riskLevel = 'critical'

  // Parse address from string
  const addressParts = (item.registered_address || '').split(',').map(s => s.trim())
  const address = {
    street: addressParts[0] || '',
    city: addressParts[1] || 'Unknown',
    state: addressParts[2] || 'Unknown',
    pincode: addressParts[3] || '',
    country: 'India'
  }

  return {
    business: {
      id: item.id,
      name: item.business_name,
      pan: item.pan,
      gstin: item.gstin || '',
      industry: item.industry_sector || 'Unknown',
      businessType: item.business_type as any || 'private_limited',
      registrationDate: item.registration_date,
      contactEmail: item.contact_email || '',
      contactPhone: item.contact_phone || '',
      address,
      ownerId: '',
      createdAt: item.created_at,
      updatedAt: item.created_at
    },
    score: {
      id: item.id,
      businessId: item.id,
      tlbhScore: score,
      band: band as any,
      confidence: parseFloat(item.confidence || '85'),
      dimensionScores: {
        legal: { score: Number((score * 0.95).toFixed(2)), weight: 0.25, explanation: 'Legal compliance', verificationPoints: [], trend: 'stable', lastUpdated: item.calculated_at || new Date().toISOString() },
        financial: { score: Number((score * 0.98).toFixed(2)), weight: 0.30, explanation: 'Financial health', verificationPoints: [], trend: 'stable', lastUpdated: item.calculated_at || new Date().toISOString() },
        operational: { score: Number((score * 1.02).toFixed(2)), weight: 0.25, explanation: 'Operational efficiency', verificationPoints: [], trend: 'stable', lastUpdated: item.calculated_at || new Date().toISOString() },
        market: { score: Number((score * 0.97).toFixed(2)), weight: 0.20, explanation: 'Market presence', verificationPoints: [], trend: 'stable', lastUpdated: item.calculated_at || new Date().toISOString() }
      },
      redFlags: [],
      calculatedAt: item.calculated_at || new Date().toISOString(),
      calculationVersion: '1.0.0',
      dataSourcesUsed: ['MCA', 'GSTN'],
      validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    },
    riskLevel,
    lastUpdated: item.calculated_at || item.created_at,
    alerts: 0,
    trend: 'stable'
  }
}

export function BusinessSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '')
  const [isLoading, setIsLoading] = useState(false)
  const [results, setResults] = useState<BusinessSearchResult[]>([])
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessSearchResult | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState({
    industry: '',
    businessType: '',
    scoreRange: { min: 0, max: 100 },
    riskLevel: '',
    location: '',
    registrationDateRange: { start: '', end: '' }
  })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasMore: false
  })
  const showToast = useToast()

  // Only load results if there's a search query in URL params
  useEffect(() => {
    const queryFromUrl = searchParams.get('q')
    if (queryFromUrl) {
      setSearchQuery(queryFromUrl)
      loadBusinesses()
    }
  }, [])

  const loadBusinesses = async (page: number = 1) => {
    setIsLoading(true)
    try {
      const params: any = {
        page,
        limit: pagination.limit
      }

      // Add search query if present
      if (searchQuery) {
        params.search = searchQuery
      }

      // Add score range filter
      if (filters.scoreRange.min > 0 || filters.scoreRange.max < 100) {
        params.minScore = filters.scoreRange.min
        params.maxScore = filters.scoreRange.max
      }

      const response = await businessService.getBusinesses(params)
      
      // Convert API response to component format
      const convertedResults = response.businesses.map(convertApiToBusinessResult)
      
      setResults(convertedResults)
      setPagination(response.pagination)
      
      // Update URL
      if (searchQuery) {
        setSearchParams({ q: searchQuery, page: page.toString() })
      } else {
        setSearchParams({ page: page.toString() })
      }
    } catch (error) {
      console.error('Failed to load businesses:', error)
      showToast.showToast({
        type: 'error',
        title: 'Load Failed',
        message: 'Unable to load businesses. Please try again.'
      })
      setResults([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = async () => {
    loadBusinesses(1)
  }

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters)
    // Reload with new filters
    loadBusinesses(1)
  }

  const applyFilters = (appliedFilters: typeof filters) => {
    // Filters are now applied in loadBusinesses function
    loadBusinesses(1)
  }

  const handleExportResults = () => {
    // Simulate export functionality
    showToast.showToast({
      type: 'success',
      title: 'Export Started',
      message: `Exporting ${pagination.total} businesses to CSV.`
    })
  }

  const handleViewBusiness = (business: BusinessSearchResult) => {
    setSelectedBusiness(business)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handlePageChange = (newPage: number) => {
    loadBusinesses(newPage)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Business Search
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Search and analyze businesses for lending decisions
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExportResults}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by business name, PAN, or GSTIN..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-4 py-3 border rounded-md text-sm font-medium transition-colors ${
                showFilters
                  ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </button>
            
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Search
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <BusinessSearchFilters
              filters={filters}
              onFiltersChange={handleFilterChange}
            />
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {isLoading ? (
            'Searching...'
          ) : (
            `Found ${pagination.total} business${pagination.total !== 1 ? 'es' : ''} (Page ${pagination.page} of ${pagination.totalPages})`
          )}
        </div>
        
        {results.length > 0 && (
          <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>Low Risk: {results.filter(r => r.riskLevel === 'low').length}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <span>Medium Risk: {results.filter(r => r.riskLevel === 'medium').length}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span>High Risk: {results.filter(r => r.riskLevel === 'high').length}</span>
            </div>
          </div>
        )}
      </div>

      {/* Search Results */}
      {results.length === 0 && !isLoading && !searchQuery ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Search for businesses
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Enter a business name, PAN, or GSTIN to find and evaluate businesses for lending decisions.
          </p>
        </div>
      ) : (
        <BusinessSearchResults
          results={results}
          isLoading={isLoading}
          onViewBusiness={handleViewBusiness}
        />
      )}

      {/* Pagination */}
      {!isLoading && results.length > 0 && pagination.totalPages > 1 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} businesses
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i
                  } else {
                    pageNum = pagination.page - 2 + i
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        pagination.page === pageNum
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasMore}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick View Modal */}
      {selectedBusiness && (
        <BusinessQuickView
          business={selectedBusiness}
          onClose={() => setSelectedBusiness(null)}
        />
      )}
    </div>
  )
}